import { isFuture, isPast, parse, parseISO, subWeeks } from "date-fns";
import { groupBy, isNil, isUndefined, keys, mapValues, toLower } from "lodash-es";
import every from "lodash-es/every";
import includes from "lodash-es/includes";
import isArray from "lodash-es/isArray";
import some from "lodash-es/some";

import { ORDER_UNIT } from "../../common/types/productOrderTypes";
import {
   type Badge,
   type BadgeKey,
   type BrandType,
   brandsDiplomis,
   brandsIsdalen,
   brandsLeverant,
   brandsTINE,
   type DiplomisBrandType,
   type FilterSettings,
   type Filters,
   hasKey,
   type IsdalenBrandType,
   isFilterAttribute,
   type Label,
   type LegalFilterKey,
   type LeverantBrandType,
   legalFilterKeys,
   PageType,
   type Product,
   type TINEBrandType
} from "../../common/types/productTypes";
import type { StoreIdType } from "../../common/types/themeTypes";
import { accumulate } from "../../common/utils";

/**
 * Takes a list of existing badges and adds another bedge if the product has the given attributes.
 */
const mapBadge = (
   existingBadges: Badge[],
   product: Product,
   certificationValue: string,
   badgeName: string,
   badgeKey: BadgeKey
) => {
   if (isNil(product.certifications)) {
      return;
   }

   const normalizedCerts = product.certifications.map((cert) => cert?.toLowerCase() || "");

   if (includes(normalizedCerts, certificationValue.toLowerCase())) {
      existingBadges.push({ name: badgeName, key: badgeKey });
   }
};

/**
 * Inspects the attributes of the products and creates Badges for matching attributes.
 * @returns An array of badges.
 */
export const mapBadges = (product: Product) => {
   const badges: Badge[] = [];
   if (product.recyclingChargeBaseUnit === 2) {
      badges.push({ name: "Pant, kr 2", key: "pant_2kr" });
   }
   if (product.recyclingChargeBaseUnit === 3) {
      badges.push({ name: "Pant, kr 3", key: "pant_3kr" });
   }
   mapBadge(badges, product, "Nyt_Norge", "Nyt Norge", "nyt_norge");
   mapBadge(badges, product, "Nokkelhullet", "Nøkkelhull", "nokkelhullet");
   return badges;
};

export const brandsInProducts = (products: Product[]): string[] => {
   const brands = new Set<string>();
   for (const product of products) {
      if (product.brand) {
         brands.add(product.brand);
      }
   }
   return Array.from(brands);
};
export const shouldShowExpiryNotice = (product: Product) =>
   !isNil(product.expiringFromDate) && product.expiringFromDate !== "" && isPast(subWeeks(parseISO(product.expiringFromDate), 6));

export const SEASONAL_PRODUCTS = [
   "5778",
   "7110",
   "7106",
   "7109",
   "345",
   "102",
   "410",
   "805",
   "6896",
   "638",
   "8687",
   "8768",
   "6746",
   "6855",
   "6862",
   "6865",
   "6746",
   "2907",
   "2978",
   "8723",
   "8771",
   "8773",
   "8815",
   "8661",
   "5254",
   "8720",
   "8956"
];

export const isNewProduct = (product: Product) => {
   if (!product.newUntilDate) {
      return false;
   }
   const newUntil = parse(product.newUntilDate, "dd.MM.yyyy", new Date());
   return isFuture(newUntil);
};

/**
 * Inspects the attributes of the products and creates Labels for matching attributes.
 * @returns An array of labels.
 */
export const mapLabels = (product: Product) => {
   const labels: Label[] = [];

   // Label: Veievare - Prio: 1
   if (product.variableWeight) {
      labels.push({
         text: "Veievare",
         color: "gray",
         position: "bottom-right",
         page: [PageType.ProductDetailsPage, PageType.ProductListPage]
      });
   }

   // Label: Nyhet - Prio: 2 (#c40076)
   if (isNewProduct(product)) {
      labels.push({
         text: "Nyhet",
         color: "blue",
         position: "top-left",
         page: [PageType.ProductDetailsPage, PageType.ProductListPage]
      });
   }

   // Label: Siste sjanse - Prio: 3 - Show 4 weeks before expiry date (#c40076)
   if (!product.expired && !isNil(product.expiringFromDate) && product.expiringFromDate.length > 0) {
      if (shouldShowExpiryNotice(product)) {
         labels.push({
            text: "Siste sjanse",
            color: "red",
            position: "top-left",
            page: [PageType.ProductDetailsPage, PageType.ProductListPage]
         });
      }
   }

   // Label: Utgått - Prio: 3 - Show after product is expired
   if (product.expired && !includes(SEASONAL_PRODUCTS, product.sku)) {
      labels.push({
         text: "Utgått",
         color: "red",
         position: "top-left",
         page: [PageType.ProductDetailsPage, PageType.ProductListPage]
      });
   }

   // Label: Avtalesortiment - Prio: 3 (#554494 + tag icon)
   if (product.inAgreementAssortment) {
      labels.push({
         text: "Avtalesortiment",
         color: "gray",
         position: "bottom-left",
         page: [PageType.ProductListPage]
      });
   }

   // Label: Økologisk - Prio: 3 (#c40076)
   const lowercaseCertifications = product.certifications?.map(toLower) ?? [];
   if (includes(lowercaseCertifications, "eu_okologi") || includes(lowercaseCertifications, "debio")) {
      labels.push({
         text: "Økologisk",
         color: "green",
         position: "top-left",
         page: [PageType.ProductListPage, PageType.ProductDetailsPage]
      });
   }

   // Label: Egne frister (for minitanker) - Prio: 5 (#4d70bc)
   if (product.miniTank) {
      labels.push({
         text: "Egne frister",
         color: "gray",
         position: "bottom-left",
         page: [PageType.ProductListPage, PageType.ProductDetailsPage]
      });
   }

   if (includes(SEASONAL_PRODUCTS, product.sku)) {
      labels.push({
         text: "Sesong",
         color: "moss",
         position: "top-left",
         page: [PageType.ProductListPage, PageType.ProductDetailsPage]
      });
   }

   return labels;
};

/**
 * Returns a boolean indicating whether the product matches the active filters.
 */
export const matchesFilter = (product: Product, activeFilters: Filters): boolean => {
   return every(activeFilters, (selectedValues, field) => {
      // Type-guards to make sure field name sent to filter function are "filterable"
      if (!hasKey(product, field) || !isFilterAttribute(field)) {
         return false;
      }
      if (!selectedValues?.length) {
         return true;
      }
      const productValue = product[field];
      return some(selectedValues, (selectedValue) =>
         isArray(productValue) ? includes(productValue, selectedValue) : productValue === selectedValue
      );
   });
};

const isMultiValueField = (field: LegalFilterKey) => ["certifications", "tastes", "tasteGroups", "allergyInfo"].includes(field);
/**
 * Creates a list of unique values for a specific product attribute given the name of the field and a list of products. Used to build one filter
 * for each filterable attribute.
 *
 * @returns Object with field key, field name and a object with one key for each avaialble value and a list of SKUs that have this value
 */
export const buildFilter = (products: Product[] | undefined, field: LegalFilterKey, name: string): FilterSettings | null => {
   if (isUndefined(products)) {
      return null;
   }

   // Remove products that dont have this field set or has an empty array for this field
   const validProductsForFilters = products.filter((p) => !isNil(p[field]) && (p[field]?.length ?? 0) > 0);

   // If a field given is a field that can have multiple values, we split the product into multiple products, one for each value
   // in array (for instance, one products with two certifications turns into two separate products in this chain)
   // The filter excludes values that are falsey
   const skuAndFieldValues = validProductsForFilters
      .flatMap((p) =>
         isMultiValueField(field)
            ? ((p[field] as string[]) ?? []).map((c) => ({ sku: p.sku, value: c }))
            : [{ sku: p.sku, value: p[field] }]
      )
      .filter((skuAndFieldValue) => skuAndFieldValue.value);

   // Group all products based on their value in field
   const groupedByValue = groupBy(skuAndFieldValues, "value");

   const knownValues = knownFilterValues(field);
   const values = knownValues ? knownValues.filter((value) => value in groupedByValue) : Object.keys(groupedByValue);

   const groupedByFieldValue: { [fieldValue: string]: string[] } = mapValues(groupedByValue, "sku");

   if (keys(groupedByFieldValue).length === 0) {
      return null;
   }

   const optional = field === "customerType" ? { radioDefault: "Isbar" } : {};

   return {
      field,
      name,
      options: groupedByFieldValue,
      values,
      ...optional
   };
};

export const getRelevantFilterSettings = (
   fullSettings: FilterSettings[],
   effectiveFilters: Filters,
   products: Product[]
): FilterSettings[] => {
   const relevantFilterSettings = fullSettings
      .map((settings) => {
         if (settings.radioDefault) {
            return settings;
         }
         let testFilters = { ...effectiveFilters };
         const filterForSettings = effectiveFilters[settings.field];
         if (filterForSettings) {
            const { [settings.field]: _, ...otherFilters } = effectiveFilters;
            testFilters = otherFilters;
         }
         const productsFilteredOnOtherFilters = products.filter((product) => matchesFilter(product, testFilters));
         return buildFilter(productsFilteredOnOtherFilters, settings.field, settings.name);
      })
      .filter(
         (f) => (f && f.values.length > 0 && f.values.length < 11) || f?.field === "tasteGroups" || f?.field === "brandGroup"
      ) as FilterSettings[];

   const hasTastes = relevantFilterSettings.some((filterSetting) => filterSetting.field === "tastes");
   const hasTastGroupsEffectiveFilter = (effectiveFilters.tasteGroups?.length || 0) > 0;

   return legalFilterKeys
      .map((key) => relevantFilterSettings.find((filter) => filter.field === key))
      .filter(
         (filter) =>
            filter &&
            !(hasTastes && !hasTastGroupsEffectiveFilter && filter.field === "tasteGroups") &&
            !(hasTastGroupsEffectiveFilter && filter.field === "tastes")
      ) as FilterSettings[];
};

export const hasDiscount = (product: Product): boolean => product.priceBeforeDiscount !== product.price;

export const isDistributionPack = (unit: ORDER_UNIT): boolean => includes([ORDER_UNIT.D, ORDER_UNIT.DFP], unit);

const nbsp = String.fromCodePoint(0x00a0);
const _half = String.fromCodePoint(0x00bd); // ½

/**
 * Trims the input name and replaces any whitespace before units (kg, g, l, ml)
 * at the end of the string with a non-breaking space to prevent line breaks.
 * For example, "Milk 1 l" becomes "Milk 1<nbsp>l".
 */
export const fixDanglingUnits = (name: string) => {
   return name ? name.trim().replace(/\s+(kg|g|l|ml|dl|liter|%)(\s|$)/gi, `${nbsp}$1$2`) : name;
};

export function prioritizeAgreementAssortment(products: Product[] | undefined, shouldPrioritize: boolean): Product[] {
   const safeProducts = products ?? [];
   return shouldPrioritize
      ? safeProducts.toSorted((a, b) => Number(b.inAgreementAssortment) - Number(a.inAgreementAssortment))
      : safeProducts;
}

export const filter = (products: Product[], filters: Filters) => {
   return products.filter((p) => matchesFilter(p, filters));
};

type KnownTINEProductBrandType =
   | "Litago"
   | "Norvegia"
   | "Jarlsberg"
   | "Snøfrisk"
   | "Fryd"
   | "Go'morgen"
   | "Fjordland"
   | "Bremykt"
   | "Brelett"
   | "Noisy"
   | "Kos"
   | "Isklar"
   | "YT"
   | "Biola"
   | "Gryr®"
   | "Østavind"
   | "Piano"
   | "TINE Kremost"
   | "Kos";

const knownTINEProductBrandToBrand: Record<KnownTINEProductBrandType, TINEBrandType> = {
   Litago: "Litago",
   Norvegia: "Norvegia",
   Jarlsberg: "Jarlsberg",
   Snøfrisk: "Snofrisk",
   Fryd: "Dovre",
   "Go'morgen": "GoMorgen",
   Fjordland: "Fjordland",
   Bremykt: "Fjordland",
   Brelett: "Fjordland",
   Noisy: "Noisy",
   Isklar: "Isklar",
   YT: "YT",
   Biola: "Biola",
   "Gryr®": "Gryr",
   Østavind: "Ostavind",
   Piano: "Piano",
   "TINE Kremost": "Kremgo",
   Kos: "Kos"
};

const knownTINEProductBrands = Object.keys(knownTINEProductBrandToBrand) as KnownTINEProductBrandType[];

const brandExceptions: Partial<Record<TINEBrandType, string[]>> = {
   TINE: ["2195", "2182", "3076", "3078", "3081", "3095", "3096", "3100", "3150"],
   Other: ["4565", "4580", "79"]
} as const;

const getBrandException = (sku: string): TINEBrandType | undefined => {
   return Object.keys(brandExceptions).find((brand) => brandExceptions[brand as TINEBrandType]?.includes(sku)) as
      | TINEBrandType
      | undefined;
};

const tineBrandResolver = (product: Product): TINEBrandType => {
   const { brand, name, sku } = product;
   const exceptionBrand = getBrandException(sku);
   if (exceptionBrand) {
      return exceptionBrand;
   }
   if (/yoplait/i.test(name)) {
      return "Yoplait";
   }
   if (brand) {
      if (brand === "Fjordland") {
         if (/kos/i.test(name)) {
            return "Kos";
         }
      }
      if (knownTINEProductBrands.includes(brand as KnownTINEProductBrandType)) {
         return knownTINEProductBrandToBrand[brand as KnownTINEProductBrandType];
      }
      if (/sunniva/i.test(brand)) {
         return "Sunniva";
      }
   }
   if (/selbu/i.test(name)) {
      return "SelbuBlaa";
   }

   if (!brand) {
      if (/(tine)|(norzola)|(taffel)|(ridder)/i.test(name)) {
         return "TINE";
      }
      if (/gryr/i.test(name)) {
         return "Gryr";
      }
      if (/norvegia/i.test(name)) {
         return "Norvegia";
      }
      return "Other";
   }

   if (brand === "Ostecompagniet") {
      return "Other";
   }

   return "TINE";
};

const diplomisBrandResolver = (_product: Product): DiplomisBrandType => {
   return "Diplomis";
};

const leverantBrandResolver = (_product: Product): LeverantBrandType => {
   return "TINE";
};

const isdalenBrandResolver = (_product: Product): IsdalenBrandType => {
   return "Kulinaris";
};

const brandsByStoreId: Record<StoreIdType, readonly BrandType[]> = {
   tinehandel: brandsTINE,
   diplomis: brandsDiplomis,
   leverant: brandsLeverant,
   isdalen: brandsIsdalen
};

export const getBrandSort = (storeId: StoreIdType) => {
   const brands: string[] = Array.from(brandsByStoreId[storeId]);
   const indicesByValue = brands.map((value, index) => ({ [value]: index })).reduce(accumulate<Record<string, number>>, {});
   const compare = (value1: string, value2: string): number => {
      if (value1 in indicesByValue) {
         if (value2 in indicesByValue) {
            return indicesByValue[value1] - indicesByValue[value2];
         }
         return -1;
      }
      if (value2 in indicesByValue) {
         return 1;
      }
      return value1 === value2 ? 0 : value1 < value2 ? -1 : 1;
   };

   return (brands: string[]) => [...brands].sort(compare);
};

export const getBrandResolver = (storeId: StoreIdType) => {
   switch (storeId) {
      case "tinehandel":
         return tineBrandResolver;
      case "diplomis":
         return diplomisBrandResolver;
      case "leverant":
         return leverantBrandResolver;
      case "isdalen":
         return isdalenBrandResolver;
      default:
         return tineBrandResolver;
   }
};

type WeightRangeValue = "under 200 g" | "200 g - 399 g" | "400 g - 599 g" | "600 g - 999 g" | "1 kg - 2 kg" | "over 2 kg";

const weightRangeValues: WeightRangeValue[] = [
   "under 200 g",
   "200 g - 399 g",
   "400 g - 599 g",
   "600 g - 999 g",
   "1 kg - 2 kg",
   "over 2 kg"
];

const weightRanges: Record<WeightRangeValue, [number, number]> = {
   "under 200 g": [0, 200],
   "200 g - 399 g": [200, 400],
   "400 g - 599 g": [400, 600],
   "600 g - 999 g": [600, 1000],
   "1 kg - 2 kg": [1000, 2001],
   "over 2 kg": [2001, Number.MAX_SAFE_INTEGER]
};

export const getWeightRange = (product: Product) => {
   const { weight } = product;
   const gramWeight = weight * 1000;
   if (typeof weight !== "number" || weight < 0) {
      return null;
   }
   for (const rangeKey of weightRangeValues) {
      const range = weightRanges[rangeKey];
      if (gramWeight >= range[0] && gramWeight < range[1]) {
         return rangeKey;
      }
   }
   return null;
};

type VolumeRangeValue =
   | "Under 1/2 liter"
   | "1/2 - under 1 liter"
   | "1 liter"
   | "1,5 / 1,75 liter"
   | "2 - 5 liter"
   | "10 liter"
   | "Fra 100 liter";
type KnownSize =
   | "Kuvert"
   | "Opp til 500 g"
   | "none"
   | "Opp til 1/2 liter"
   | "2 liter - 5 liter"
   | "500 g - 1 kg"
   | "1/2 liter - 1 liter"
   | "1 liter"
   | "Fra 100 liter"
   | "1 kg - 5 kg"
   | "Over 10 kg"
   | "1,5 liter"
   | "1,75 liter"
   | "5 kg - 10 kg"
   | "Bag in box (10 liter)"
   | "10 liter";

const volumeRangeValues: VolumeRangeValue[] = [
   "Under 1/2 liter",
   "1/2 - under 1 liter",
   "1 liter",
   "1,5 / 1,75 liter",
   "2 - 5 liter",
   "10 liter",
   "Fra 100 liter"
];

const knownSizeToVolumeRangeValue: Partial<Record<KnownSize, VolumeRangeValue>> = {
   "Opp til 1/2 liter": "Under 1/2 liter",
   "1/2 liter - 1 liter": "1/2 - under 1 liter",
   "1 liter": "1 liter",
   "1,5 liter": "1,5 / 1,75 liter",
   "1,75 liter": "1,5 / 1,75 liter",
   "2 liter - 5 liter": "2 - 5 liter",
   "10 liter": "10 liter",
   "Bag in box (10 liter)": "10 liter",
   "Fra 100 liter": "Fra 100 liter"
};

const allowConversionToVolumeRange = (product: Product) => {
   const { name } = product;
   return (
      /biola/i.test(name) ||
      /litago/i.test(name) ||
      /yogh/i.test(name) ||
      /pudding/i.test(name) ||
      /kaffefløte/i.test(name) ||
      /kaffemelk/i.test(name) ||
      /cottage/i.test(name) ||
      /kesam/i.test(name) ||
      /kvarg/i.test(name)
   );
};

const getVolumeFromPackaging = (packaging: string): number | undefined => {
   const res = /([\d,]+)\s?l/i.exec(packaging);
   if (res && res.length > 1) {
      return Number(res[1].replace(",", "."));
   }
};

export const getVolumeRange = (product: Product): VolumeRangeValue | null => {
   const { size, weight, packaging, sku } = product;
   if (["7147"].includes(sku)) {
      return "Under 1/2 liter";
   }
   if (["7231", "7232"].includes(sku)) {
      return "1 liter";
   }
   const volumeRange = knownSizeToVolumeRangeValue[size as KnownSize];
   if (!volumeRange) {
      const knownSize: KnownSize | null = size as KnownSize | null;
      if (allowConversionToVolumeRange(product)) {
         if (knownSize === "Opp til 500 g") {
            if (weight >= 0.5) {
               return "1/2 - under 1 liter";
            }
            return "Under 1/2 liter";
         }
         if (knownSize === "500 g - 1 kg") {
            if (weight >= 1) {
               return "1 liter";
            }
            return "1/2 - under 1 liter";
         }
         if (weight < 0.5) {
            return "Under 1/2 liter";
         }
         if (weight >= 0.5 && weight <= 0.9) {
            return "1/2 - under 1 liter";
         }
         if (weight > 0.9 && weight < 1.1) {
            return "1 liter";
         }
         if (weight > 1.4 && weight < 1.8) {
            return "1,5 / 1,75 liter";
         }
         if (weight > 2 && weight < 5.5) {
            return "2 - 5 liter";
         }
         if (weight > 10 && weight < 11) {
            return "10 liter";
         }
         if (weight > 100) {
            return "Fra 100 liter";
         }
      }

      if (packaging) {
         const volumeFromPackaging = getVolumeFromPackaging(packaging);
         if (typeof volumeFromPackaging === "number") {
            if (volumeFromPackaging < 0.5) {
               return "Under 1/2 liter";
            }
            if (volumeFromPackaging < 1) {
               return "1/2 - under 1 liter";
            }
            if (volumeFromPackaging < 1.1) {
               return "1 liter";
            }
            if (volumeFromPackaging < 1.8) {
               return "1,5 / 1,75 liter";
            }
            if (volumeFromPackaging < 6) {
               return "2 - 5 liter";
            }
            if (volumeFromPackaging < 11) {
               return "10 liter";
            }
            if (volumeFromPackaging > 99) {
               return "Fra 100 liter";
            }
         }
      }
   }

   return volumeRange || null;
};

type SizeRangeValue = "Under 1/2 kg/liter" | "1/2 - under 1 kg/liter" | "1 kg/liter" | "Over 1 - 5 kg/liter" | "Over 5 kg/liter";

const sizeRangeValues: SizeRangeValue[] = [
   "Under 1/2 kg/liter",
   "1/2 - under 1 kg/liter",
   "1 kg/liter",
   "Over 1 - 5 kg/liter",
   "Over 5 kg/liter"
];

export const getSizeRange = (product: Product): SizeRangeValue | null => {
   const { weight, size } = product;
   const knownSize = size as KnownSize;
   if (
      knownSize === "Fra 100 liter" ||
      knownSize === "Over 10 kg" ||
      knownSize === "Bag in box (10 liter)" ||
      knownSize === "5 kg - 10 kg"
   ) {
      return "Over 5 kg/liter";
   }
   if (knownSize === "1 kg - 5 kg" && weight >= 1.1) {
      return "Over 1 - 5 kg/liter";
   }
   if (!weight) {
      return null;
   }
   if (weight < 0.5) {
      return "Under 1/2 kg/liter";
   }
   if (weight < 1) {
      return "1/2 - under 1 kg/liter";
   }
   if (weight < 1.1) {
      return "1 kg/liter";
   }
   if (weight < 5.5) {
      return "Over 1 - 5 kg/liter";
   }
   return "Over 5 kg/liter";
};

export const knownFilterValues = (filterKey: LegalFilterKey): string[] | null => {
   switch (filterKey) {
      case "brandGroup":
         return [...brandsTINE];
      case "weightRange":
         return weightRangeValues;
      case "volumeRange":
         return volumeRangeValues;
      case "sizeRange":
         return sizeRangeValues;
      case "tastes":
         return [...knownTastes];
      case "tasteGroups":
         return [...tasteGroups];
      case "allergyInfo":
         return [...allergyInfoValues];
      default:
         return null;
   }
};

export const tasteGroups = [
   "Naturell",
   "Sjokolade",
   "Karamell",
   "Kaffe",
   "Vanilje",
   "Krydder / urter",
   "Bær",
   "Frukt",
   "Grønnsaker",
   "Øvrige"
] as const;

export type TasteGroup = (typeof tasteGroups)[number];

export const knownTastes = [
   "Naturell",
   "Sjokolade",
   "Karamell",
   "Kaffe",
   "Vanilje",
   "Chili",
   "Krydder",
   "Urter",
   "Pepper",
   "Gurkemeie",
   "Hvitløk",
   "Ramsløk",
   "Ingefær",
   "Spisskum",
   "Nellik",
   "Kanel",
   "Dill",
   "Røde bær",
   "Bringebær",
   "Jordbær",
   "Skogsbær",
   "Kirsebær",
   "Morell",
   "Blåbær",
   "Molte",
   "Bjørnebær",
   "Tranebær",
   "Rips",
   "Frukt",
   "Eple",
   "Pære",
   "Appelsin",
   "Mandarin",
   "Sitron",
   "Lime",
   "Grapefrukt",
   "Banan",
   "Fersken",
   "Mango",
   "Granateple",
   "Pasjonsfrukt",
   "Ananas",
   "Tropisk",
   "Melon",
   "Vannmelon",
   "Guava",
   "Kokos",
   "Sviske",
   "Rabarbra",
   "Grønnsaker",
   "Gresskar",
   "Tomat",
   "Rødbet",
   "Gulrot",
   "Paprika",
   "Kantarell",
   "Honning",
   "Rom",
   "Mandel",
   "Peanøtt",
   "Bacon",
   "Skinke"
] as const;

export type KnownTaste = (typeof knownTastes)[number];

const tasteToTasteGroup: Record<KnownTaste, TasteGroup> = {
   Naturell: "Naturell",
   Sjokolade: "Sjokolade",
   Karamell: "Karamell",
   Kaffe: "Kaffe",
   Vanilje: "Vanilje",
   Chili: "Krydder / urter",
   Krydder: "Krydder / urter",
   Urter: "Krydder / urter",
   Pepper: "Krydder / urter",
   Gurkemeie: "Krydder / urter",
   Hvitløk: "Krydder / urter",
   Ramsløk: "Krydder / urter",
   Ingefær: "Krydder / urter",
   Spisskum: "Krydder / urter",
   Nellik: "Krydder / urter",
   Kanel: "Krydder / urter",
   Dill: "Krydder / urter",
   "Røde bær": "Bær",
   Bringebær: "Bær",
   Jordbær: "Bær",
   Skogsbær: "Bær",
   Kirsebær: "Bær",
   Morell: "Bær",
   Blåbær: "Bær",
   Molte: "Bær",
   Bjørnebær: "Bær",
   Tranebær: "Bær",
   Rips: "Bær",
   Frukt: "Frukt",
   Eple: "Frukt",
   Pære: "Frukt",
   Appelsin: "Frukt",
   Mandarin: "Frukt",
   Sitron: "Frukt",
   Lime: "Frukt",
   Grapefrukt: "Frukt",
   Banan: "Frukt",
   Fersken: "Frukt",
   Mango: "Frukt",
   Granateple: "Frukt",
   Pasjonsfrukt: "Frukt",
   Ananas: "Frukt",
   Tropisk: "Frukt",
   Melon: "Frukt",
   Vannmelon: "Frukt",
   Guava: "Frukt",
   Kokos: "Frukt",
   Sviske: "Frukt",
   Rabarbra: "Frukt",
   Grønnsaker: "Grønnsaker",
   Gresskar: "Grønnsaker",
   Tomat: "Grønnsaker",
   Rødbet: "Grønnsaker",
   Gulrot: "Grønnsaker",
   Paprika: "Grønnsaker",
   Kantarell: "Grønnsaker",
   Honning: "Øvrige",
   Rom: "Øvrige",
   Mandel: "Øvrige",
   Peanøtt: "Øvrige",
   Bacon: "Øvrige",
   Skinke: "Øvrige"
};

const tasteRegExps: Partial<Record<KnownTaste, RegExp>> = {
   Pasjonsfrukt: /pasjon/i,
   Rom: /\brom\b/i,
   Kaffe: /kaffe\b|kaffesmak|cappuccino/i,
   Sjokolade: /sjoko|kakao|mocha/i
};

const getTasteRegExp = (taste: KnownTaste) => {
   if (!tasteRegExps[taste]) {
      tasteRegExps[taste] = new RegExp(taste, "i");
   }
   return tasteRegExps[taste];
};

const hasTaste = (product: Product, knownTaste: KnownTaste) => {
   const { taste, name } = product;
   const re = getTasteRegExp(knownTaste);
   if (taste) {
      if (re.test(taste)) {
         return true;
      }
   }
   if (re.test(name)) {
      return true;
   }

   return false;
};
export const getTastes = (product: Product) => {
   return knownTastes.filter((taste) => hasTaste(product, taste));
};

export const getTasteGroups = (tastes: KnownTaste[]) => {
   const groups = new Set<TasteGroup>();
   for (const taste of tastes) {
      groups.add(tasteToTasteGroup[taste]);
   }
   return Array.from(groups);
};

export const getTastesAndTasteGroups = (product: Product) => {
   const exclude = ["8941", "8948", "8829", "8927", "8582"];
   if (exclude.includes(product.sku)) {
      return {
         tastes: [],
         tasteGroups: []
      };
   }
   const tastes = getTastes(product);
   const tasteGroups = getTasteGroups(tastes);
   return { tastes, tasteGroups };
};

export const allergyInfoValues = ["Glutenfri", "Laktosefri", "Laktoseredusert", "Plantebasert"] as const;

export type AllergyInfo = (typeof allergyInfoValues)[number];

const allergyInfoRegExpes: Record<AllergyInfo, RegExp> = {
   Glutenfri: /glutenfri/i,
   Laktosefri: /laktosefri/i,
   Laktoseredusert: /laktoseredusert/i,
   Plantebasert: /plante/i
};

export const getAllergyInfo = (product: Product) => {
   const { searchWord, name, description } = product;
   if (searchWord?.length) {
      return allergyInfoValues.filter(
         (value) =>
            searchWord.find((word) => allergyInfoRegExpes[value].test(word)) ||
            allergyInfoRegExpes[value].test(name) ||
            allergyInfoRegExpes[value].test(description?.toString() || "")
      );
   }
   return [];
};

const formats = [
   "Kuvert",
   "Bag-in-box",
   "Beger",
   "Skiver",
   "Blokk",
   "Spann",
   "Kartong",
   "Flaske",
   "Hjul",
   "Sprayboks",
   "Pose",
   "Pall",
   "Tank"
] as const;

export type FormatType = (typeof formats)[number];

const formatRegExpes: Partial<Record<FormatType, RegExp>> = {
   "Bag-in-box": /bag-in-box|\bbib\b/i,
   Skiver: /skive(r|t)/i
};

const getFormatRegExp = (format: FormatType) => {
   if (!formatRegExpes[format]) {
      formatRegExpes[format] = new RegExp(format, "i");
   }
   return formatRegExpes[format];
};

export const getFormat = (product: Product): FormatType | null => {
   const { packaging } = product;
   for (const format of formats) {
      if (packaging?.match(getFormatRegExp(format))) {
         return format;
      }
   }
   return null;
};
